import { useCallback } from 'react';
import supabase from '../../supabase';
import InvoicePDF from '../Invoice/InvoicePDF';
import { pdf } from '@react-pdf/renderer';
import QrCode from 'qrcode';

export const useInvoiceOperations = ({
  user,
  clients,
  invoices,
  recurringChildren,
  setInvoices,
  setRecurringChildren,
  setSendingInvoice,
  setSendingReminder,
  setUpdatingStatus,
  setEditingInvoice,
  setEditFormData,
  userProfile,
  editingInvoice,
  editFormData
}) => {
  
  // Helper functions
  const numberToWords = (num) => {
    const ones = ['', 'ONE', 'TWO', 'THREE', 'FOUR', 'FIVE', 'SIX', 'SEVEN', 'EIGHT', 'NINE'];
    const teens = ['TEN', 'ELEVEN', 'TWELVE', 'THIRTEEN', 'FOURTEEN', 'FIFTEEN', 'SIXTEEN', 'SEVENTEEN', 'EIGHTEEN', 'NINETEEN'];
    const tens = ['', '', 'TWENTY', 'THIRTY', 'FORTY', 'FIFTY', 'SIXTY', 'SEVENTY', 'EIGHTY', 'NINETY'];

    const convertHundreds = (n) => {
      let result = '';
      if (n >= 100) {
        result += ones[Math.floor(n / 100)] + ' HUNDRED ';
        n %= 100;
      }
      if (n >= 20) {
        result += tens[Math.floor(n / 10)] + ' ';
        n %= 10;
      } else if (n >= 10) {
        result += teens[n - 10] + ' ';
        n = 0;
      }
      if (n > 0) {
        result += ones[n] + ' ';
      }
      return result;
    };

    const convertLargeNumber = (n) => {
      if (n === 0) return '';
      let result = '';
      let crores = Math.floor(n / 10000000);
      n %= 10000000;
      let lakhs = Math.floor(n / 100000);
      n %= 100000;
      let thousands = Math.floor(n / 1000);
      n %= 1000;

      if (crores > 0) result += convertHundreds(crores) + 'CRORE ';
      if (lakhs > 0) result += convertHundreds(lakhs) + 'LAKH ';
      if (thousands > 0) result += convertHundreds(thousands) + 'THOUSAND ';
      if (n > 0) result += convertHundreds(n);

      return result;
    };

    if (!num || isNaN(num)) return 'ZERO RUPEES ONLY';
    
    const integerPart = Math.floor(num);
    const decimalPart = Math.round((num - integerPart) * 100);
    let result = integerPart === 0 ? 'ZERO' : convertLargeNumber(integerPart).trim();

    result += ' RUPEES';
    if (decimalPart > 0) {
      result += ' AND ' + convertHundreds(decimalPart).trim() + ' PAISE';
    }
    result += ' ONLY';
    return result;
  };

  const getLocalItemTotal = (item) => {
    if (!item) return 0;
    const qty = typeof item.quantity === 'string' ? parseFloat(item.quantity) || 0 : item.quantity || 0;
    const price = typeof item.rate_per_item === 'string' ? parseFloat(item.rate_per_item) || 0 : item.rate_per_item || 0;
    return qty * price;
  };

  const calculateLocalItemTax = (item, sgstRate = 0, cgstRate = 0) => {
    if (!item) return 0;
    const base = getLocalItemTotal(item);
    return base * ((sgstRate + cgstRate) / 100);
  };

  const calculateLocalSummary = (items) => {
    if (!Array.isArray(items)) return {
      subtotal: 0,
      totalTax: 0,
      total: 0,
      sgstTotal: 0,
      cgstTotal: 0,
      igstTotal: 0,
      discountAmount: 0
    };

    let subtotal = 0, totalTax = 0;
    let sgstTotal = 0, cgstTotal = 0, igstTotal = 0;
    
    items.forEach(item => {
      if (item) {
        const base = getLocalItemTotal(item);
        const tax = parseFloat(item.total_tax) || 0;
        subtotal += base;
        totalTax += tax;
        
        if (tax > 0) {
          sgstTotal += tax / 2;
          cgstTotal += tax / 2;
        }
      }
    });

    const total = subtotal + totalTax;
    
    return { 
      subtotal, 
      totalTax, 
      total, 
      sgstTotal, 
      cgstTotal, 
      igstTotal,
      discountAmount: 0 
    };
  };

  const generateUPIQRCode = async (upiId, amount, merchantName, invoiceNumber) => {
    try {
      if (!upiId || !amount) return null;
      
      const upiString = `upi://pay?pa=${upiId}&am=${amount}&pn=${encodeURIComponent(merchantName || 'Merchant')}&tn=${encodeURIComponent(`Invoice ${invoiceNumber}`)}&cu=INR`;
      return await QrCode.toDataURL(upiString, {
        width: 200,
        margin: 1,
        color: { dark: '#000000', light: '#FFFFFF' },
        errorCorrectionLevel: 'M'
      });
    } catch (error) {
      console.error('Error generating UPI QR Code:', error);
      return null;
    }
  };

  const validateInvoiceData = (data) => {
    const { invData, itemsData, clientData, userProfileData, fromInfo, selectedClient, invoiceInfo } = data;

    // Check required invoice data
    if (!invData) {
      throw new Error('Invoice data is missing');
    }

    // Check required user profile data
    if (!userProfileData) {
      throw new Error('User profile data is missing');
    }

    // Check required client data
    if (!clientData) {
      throw new Error('Client data is missing');
    }

    // Validate from info
    if (!fromInfo.name && !fromInfo.company_name) {
      throw new Error('From information must include either name or company name');
    }

    // Validate selected client
    if (!selectedClient.client_name) {
      throw new Error('Client name is required');
    }

    // Validate invoice info
    if (!invoiceInfo.number) {
      throw new Error('Invoice number is required');
    }

    return true;
  };

  const sendInvoiceEmail = useCallback(async (invoice) => {
    if (!invoice) {
      console.error('No invoice provided to sendInvoiceEmail');
      return;
    }

    if (!user || !user.id) {
      console.error('No user available for sendInvoiceEmail');
      alert('User not authenticated. Please log in and try again.');
      return;
    }

    const clientName = clients[invoice.client_id] || 'Client';
    const invoiceNumber = invoice.invoice_number || 'Invoice';
    
    console.log(`Starting email send for invoice ${invoiceNumber} to client ${clientName}`);
    setSendingInvoice(prev => ({ ...prev, [invoice.id]: true }));
    
    try {
      // Fetch all required data with error handling for each request
      console.log('Fetching invoice data...');
      const [invoiceResult, itemsResult, clientResult, bankResult, userResult] = await Promise.all([
        supabase.from('invoices').select('*').eq('id', invoice.id).single(),
        supabase.from('invoice_items').select('*').eq('invoice_id', invoice.id).order('id'),
        supabase.from('clients').select('*').eq('id', invoice.client_id).single(),
        supabase.from('banks').select('*').eq('user_id', user.id),
        supabase.from('users').select('*').eq('id', user.id).single()
      ]);

      // Check for errors in data fetching
      if (invoiceResult.error) {
        throw new Error(`Failed to fetch invoice: ${invoiceResult.error.message}`);
      }
      if (itemsResult.error) {
        throw new Error(`Failed to fetch invoice items: ${itemsResult.error.message}`);
      }
      if (clientResult.error) {
        throw new Error(`Failed to fetch client data: ${clientResult.error.message}`);
      }
      if (bankResult.error) {
        console.warn('Failed to fetch bank data:', bankResult.error.message);
      }
      if (userResult.error) {
        throw new Error(`Failed to fetch user data: ${userResult.error.message}`);
      }

      const invData = invoiceResult.data;
      const itemsData = itemsResult.data || [];
      const clientData = clientResult.data;
      const bankData = bankResult.data || [];
      const userProfileData = userResult.data;

      console.log('Fetched data:', {
        invoice: !!invData,
        items: itemsData.length,
        client: !!clientData,
        banks: bankData.length,
        user: !!userProfileData
      });

      // Validate email address
      const clientEmail = clientData?.client_email || clientData?.email;
      if (!clientEmail) {
        throw new Error('Client email not found. Please add an email address for this client.');
      }

      // Build data objects with proper fallbacks
      const fromInfo = {
        company_name: invData?.company_name || userProfileData?.company_name || 'Your Company',
        name: invData?.from_name || userProfileData?.full_name || userProfileData?.name || 'Your Name',
        address: invData?.from_address || userProfileData?.address || 'Your Address',
        phone: invData?.from_phone || userProfileData?.phone || '',
        email: invData?.from_email || userProfileData?.email || user?.email || '',
        contact_email: invData?.from_email || userProfileData?.email || user?.email || '',
        gst_number: invData?.business_number || userProfileData?.gst_number || ''
      };

      const selectedClient = {
        client_name: clientData?.client_name || 'Unnamed Client',
        client_company_name: clientData?.client_company_name || '',
        address: clientData?.address || '',
        email: clientEmail,
        client_email: clientEmail,
        client_gst_number: clientData?.client_gst_number || ''
      };

      const invoiceInfo = {
        number: invData?.invoice_number || '',
        issueDate: invData?.issue_date ? new Date(invData.issue_date).toLocaleDateString() : '',
        dueDate: invData?.due_date ? new Date(invData.due_date).toLocaleDateString() : ''
      };

      const items = itemsData.map(item => ({
        product_name: item?.name || '',
        description: item?.description || '',
        hsn_code: item?.hsn_sac || '',
        hsn: item?.hsn_sac || '',
        quantity: item?.quantity || 0,
        unit_price: item?.rate_per_item || 0,
        rate_per_item: item?.rate_per_item || 0,
        total_tax: item?.total_tax || 0,
        total: item?.total || 0
      }));

      const selectedBank = bankData && bankData.length > 0 ? {
        account_number: bankData[0]?.account_number || '',
        ifsc_code: bankData[0]?.ifsc_code || '',
        bank_name: bankData[0]?.bank_name || '',
        upi_id: bankData[0]?.upi_id || '',
        account_holder_name: bankData[0]?.account_holder_name || fromInfo?.name || fromInfo?.company_name || '',
        branch_name: bankData[0]?.branch || ''
      } : null;

      // Validate all required data before proceeding
      try {
        validateInvoiceData({
          invData,
          itemsData,
          clientData,
          userProfileData,
          fromInfo,
          selectedClient,
          invoiceInfo
        });
      } catch (validationError) {
        throw new Error(`Data validation failed: ${validationError.message}`);
      }

      const localSummary = calculateLocalSummary(items);
      console.log('Calculated summary:', localSummary);

      // Generate QR code with error handling
      let qrCodeDataURL = null;
      if (selectedBank?.upi_id && localSummary.total < 100001 && localSummary.total > 0) {
        console.log('Generating UPI QR code...');
        qrCodeDataURL = await generateUPIQRCode(
          selectedBank.upi_id, 
          localSummary.total.toFixed(2), 
          fromInfo?.company_name || fromInfo?.name || 'Merchant', 
          invoiceInfo.number
        );
        if (qrCodeDataURL) {
          console.log('QR code generated successfully');
        }
      }

      const invoiceData = {
        fromInfo,
        selectedClient,
        newClient: null,
        invoiceInfo,
        termsAndConditions: invData?.notes ? [invData.notes] : ['Payment due within 30 days'],
        userProfile: {
          ...userProfileData,
          logo_url: userProfileData?.logo_url || userProfile?.logo_url || null
        },
        selectedBank
      };

      console.log('Creating PDF component with validated data...');
      
      // Create PDF component with error boundary
      const doc = (
        <InvoicePDF
          invoiceData={invoiceData}
          items={items}
          localSummary={localSummary}
          numberToWords={numberToWords}
          getLocalItemTotal={getLocalItemTotal}
          calculateLocalItemTax={(item) => calculateLocalItemTax(item, 9, 9)}
          showTaxPerItemColumn={localSummary.totalTax > 0}
          showHsnColumn={true}
          hasTax={localSummary.totalTax > 0}
          hasdiscount={false}
          discount={0}
          taxType="SGST_CGST"
          sgstRate={9}
          cgstRate={9}
          igstRate={0}
          selectedBank={selectedBank}
          qrCodeDataURL={qrCodeDataURL}
          showValueColumn={localSummary.totalTax > 0}
        />
      );

      console.log('Generating PDF blob...');
      let pdfBlob;
      try {
        pdfBlob = await pdf(doc).toBlob();
        console.log('PDF blob generated successfully, size:', pdfBlob.size);
      } catch (pdfError) {
        console.error('PDF generation error:', pdfError);
        throw new Error(`Failed to generate PDF: ${pdfError.message}`);
      }

      console.log('Converting PDF to base64...');
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          try {
            resolve(reader.result.split(',')[1]);
          } catch (error) {
            reject(new Error('Failed to convert PDF to base64'));
          }
        };
        reader.onerror = () => reject(new Error('FileReader error'));
        reader.readAsDataURL(pdfBlob);
      });

      console.log('Sending email via Supabase Edge Function...');
      const res = await fetch(
        'https://dzwtnmlmjwbwqevbhwnv.supabase.co/functions/v1/send-invoice-email',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            from: 'no-reply@bossbyko.com',
            to: clientEmail,
            subject: `Invoice ${invoiceNumber}`,
            html: `<p>Hello ${clientName},</p><p>Please find attached invoice <strong>${invoiceNumber}</strong>.</p><p>Best regards,<br>${fromInfo.company_name || fromInfo.name}</p>`,
            attachments: [{
              filename: `invoice-${invoiceNumber}.pdf`,
              content: base64,
              type: 'application/pdf'
            }]
          })
        }
      );

      if (!res.ok) {
        throw new Error(`Email service error: ${res.status} ${res.statusText}`);
      }

      const result = await res.json();
      if (!result.success) {
        throw new Error(result.error || 'Email service returned failure');
      }
      
      console.log('Email sent successfully, updating database...');
      const { error: updateErr } = await supabase
        .from('invoices')
        .update({ email_sent: true })
        .eq('id', invoice.id)
        .eq('user_id', user.id);
      
      if (updateErr) {
        console.error('Database update error:', updateErr);
        throw new Error(`Failed to update invoice status: ${updateErr.message}`);
      }
      
      // Update local state
      setInvoices(prev => prev.map(i =>
        i.id === invoice.id ? { ...i, email_sent: true } : i
      ));
      
      setRecurringChildren(prev => prev.map(i =>
        i.id === invoice.id ? { ...i, email_sent: true } : i
      ));
      
      console.log('Invoice email process completed successfully');
      alert('Invoice email with PDF sent successfully!');
    } catch (err) {
      console.error('Send invoice error details:', {
        message: err.message,
        stack: err.stack,
        invoice: invoice.id,
        client: invoice.client_id
      });
      alert(`Failed to send invoice email: ${err.message}`);
    } finally {
      setSendingInvoice(prev => ({ ...prev, [invoice.id]: false }));
    }
  }, [user, clients, userProfile, setInvoices, setRecurringChildren, setSendingInvoice]);

  const sendReminder = useCallback(async (invoice) => {
    if (!invoice) return;
    const clientName = clients[invoice.client_id] || 'Client';
    const invoiceNumber = invoice.invoice_number || 'Invoice';
    setSendingReminder(prev => ({ ...prev, [invoice.id]: true }));
    try {
      const { data: clientData, error: cErr } = await supabase
        .from('clients')
        .select('client_email')
        .eq('id', invoice.client_id)
        .single();
      if (cErr || !clientData.client_email) throw new Error('Client email not found');

      const res = await fetch(
        'https://dzwtnmlmjwbwqevbhwnv.supabase.co/functions/v1/send-invoice-email',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            from: 'no-reply@bossbyko.com',
            to: clientData.client_email,
            subject: `Reminder: Invoice ${invoiceNumber} is still unpaid`,
            html: `<p>Hello ${clientName},</p><p>This is a reminder that invoice <strong>${invoiceNumber}</strong> is still marked as unpaid. Please make the payment as soon as possible.</p>`
          })
        }
      );
      const result = await res.json();
      if (!result.success) throw new Error(result.error || 'Failed');
      alert('Reminder email sent!');
    } catch (err) {
      console.error('Reminder error:', err);
      alert('Failed to send reminder.');
    } finally {
      setSendingReminder(prev => ({ ...prev, [invoice.id]: false }));
    }
  }, [clients, setSendingReminder]);

  const handleStatusUpdate = useCallback(async (invoiceId, newStatus) => {
    const invoice = invoices.find(i => i.id === invoiceId) || recurringChildren.find(i => i.id === invoiceId);
    
    if (!invoice?.email_sent) {
      alert('Cannot change status. Invoice must be sent first.');
      return;
    }

    setUpdatingStatus(prev => ({ ...prev, [invoiceId]: true }));
    try {
      const { error: uErr } = await supabase
        .from('invoices')
        .update({ status: newStatus })
        .eq('id', invoiceId)
        .eq('user_id', user.id);
      if (uErr) {
        if (uErr.code === '23514') {
          alert(`Status "${newStatus}" not allowed.`);
          return;
        }
        throw uErr;
      }

      const invNum = invoice.invoice_number || 'Unnamed Invoice';

      if (newStatus === 'Paid') {
        const { data: existingIncome } = await supabase
          .from('income')
          .select('id')
          .eq('user_id', user.id)
          .eq('name', invNum)
          .limit(1);

        if (!existingIncome || existingIncome.length === 0) {
          const clientName = clients[invoice.client_id] || 'Unknown Client';
          const today = new Date().toISOString().split('T')[0];
          const { error: iErr } = await supabase
            .from('income')
            .insert([{
              user_id: user.id,
              name: invNum,
              description: clientName,
              total_amount: invoice.total_amount || 0,
              income_date: today,
              e_status: 'i',
              client_id: invoice.client_id
            }]);
          if (iErr) {
            console.error('Income insert failed:', iErr);
            alert('Status updated but failed to record income.');
          }
        }
      } else if (newStatus === 'Unpaid') {
        const { error: dErr } = await supabase
          .from('income')
          .delete()
          .eq('user_id', user.id)
          .eq('name', invNum);
        if (dErr) {
          console.error('Income delete failed:', dErr);
          alert('Status updated but failed to remove income record.');
        }
      }

      setInvoices(prev => prev.map(i =>
        i.id === invoiceId ? { ...i, status: newStatus } : i
      ));
      
      setRecurringChildren(prev => prev.map(i =>
        i.id === invoiceId ? { ...i, status: newStatus } : i
      ));
      
    } catch (err) {
      console.error('Error updating status:', err);
      alert('Error updating invoice status. Please try again.');
    } finally {
      setUpdatingStatus(prev => ({ ...prev, [invoiceId]: false }));
    }
  }, [user, clients, invoices, recurringChildren, setInvoices, setRecurringChildren, setUpdatingStatus]);

  const startEditInvoice = useCallback((invoice) => {
    setEditingInvoice(invoice.id);
    setEditFormData({
      invoice_number: invoice.invoice_number || '',
      issue_date: invoice.issue_date || '',
      due_date: invoice.due_date || '',
      notes: invoice.notes || '',
      total_amount: invoice.total_amount || 0
    });
  }, [setEditingInvoice, setEditFormData]);

  const cancelEditInvoice = useCallback(() => {
    setEditingInvoice(null);
    setEditFormData({});
  }, [setEditingInvoice, setEditFormData]);

  const saveEditInvoice = useCallback(async () => {
    try {
      const updateData = {
        invoice_number: editFormData.invoice_number,
        issue_date: editFormData.issue_date,
        due_date: editFormData.due_date,
        notes: editFormData.notes,
        total_amount: editFormData.total_amount
      };

      const { error } = await supabase
        .from('invoices')
        .update(updateData)
        .eq('id', editingInvoice)
        .eq('user_id', user.id);

      if (error) {
        console.error('Database error:', error);
        throw error;
      }

      setInvoices(prev => prev.map(i =>
        i.id === editingInvoice ? { ...i, ...updateData } : i
      ));
      
      setRecurringChildren(prev => prev.map(i =>
        i.id === editingInvoice ? { ...i, ...updateData } : i
      ));

      setEditingInvoice(null);
      setEditFormData({});
      alert('Invoice updated successfully!');
    } catch (err) {
      console.error('Error updating invoice:', err);
      alert(`Failed to update invoice: ${err.message}`);
    }
  }, [editingInvoice, editFormData, user, setInvoices, setRecurringChildren, setEditingInvoice, setEditFormData]);

  const deleteInvoice = useCallback(async (invoice, addToIncome = false) => {
    const confirmDelete = window.confirm(
      addToIncome
        ? `Are you sure you want to ADD this to income and DELETE invoice "${invoice.invoice_number}"?`
        : `Are you sure you want to DELETE invoice "${invoice.invoice_number}"?`
    );

    if (!confirmDelete) return;

    try {
      if (addToIncome) {
        const clientName = clients[invoice.client_id] || 'Unknown Client';
        const today = new Date().toISOString().split('T')[0];

        const { error: incomeErr } = await supabase
          .from('income')
          .insert([
            {
              user_id: user.id,
              name: invoice.invoice_number,
              description: clientName,
              total_amount: invoice.total_amount || 0,
              income_date: today,
              e_status: 'i',
              client_id: invoice.client_id
            }
          ]);

        if (incomeErr) throw incomeErr;
      }

      const { error: deleteErr } = await supabase
        .from('invoices')
        .delete()
        .eq('id', invoice.id)
        .eq('user_id', user.id);

      if (deleteErr) throw deleteErr;

      setInvoices((prev) => prev.filter((i) => i.id !== invoice.id));
      setRecurringChildren((prev) => prev.filter((i) => i.id !== invoice.id));
      
      alert('Invoice deleted successfully.');
    } catch (error) {
      console.error('Delete error:', error);
      alert('Failed to delete invoice. Please try again.');
    }
  }, [user, clients, setInvoices, setRecurringChildren]);

  return {
    sendInvoiceEmail,
    sendReminder,
    handleStatusUpdate,
    deleteInvoice,
    startEditInvoice,
    cancelEditInvoice,
    saveEditInvoice
  };
};